import { computeDefaults, filterValidParamOptions, resolveSelectDefault } from '@server/dashboard-param-defaults';
import { isValidDateOnly } from '@server/time-presets';

import type { DashboardParam } from '../types';

// One implementation of these rules, shared with the server (see src/lib/dashboard-param-defaults.ts).
// Re-exported so UI callers keep importing them from this module.
export { computeDefaults, filterValidParamOptions, resolveSelectDefault };

/**
 * Known-bad stringification artifacts from JS → URL coercion.
 * URLSearchParams.set(key, undefined) produces "undefined" as a string.
 */
export const INVALID_PARAM_VALUES = new Set(['undefined', 'null', 'NaN']);

/** Parse search params into a param values record, rejecting known-bad values. */
export function searchParamsToValues(
  searchParams: URLSearchParams,
  parameters: DashboardParam[],
): Record<string, string | string[]> {
  const values: Record<string, string | string[]> = {};
  for (const param of parameters) {
    if (param.type === 'time_range') {
      // fixed_preset dates are server-resolved — never read or write them from URL.
      if (param.fixed_preset) continue;
      // Reject anything that isn't a strict YYYY-MM-DD real date — the
      // downstream display and SQL-substitution layers both assume that
      // shape. Falling through to `computeDefaults` is the safe default.
      const startKey = `${param.name}_start`;
      const endKey = `${param.name}_end`;
      const s = searchParams.get(startKey);
      const e = searchParams.get(endKey);
      if (s && isValidDateOnly(s)) values[startKey] = s;
      if (e && isValidDateOnly(e)) values[endKey] = e;
    } else if (param.type === 'multi_select') {
      const raw = searchParams.getAll(param.name);
      const filtered = raw.filter((v) => !INVALID_PARAM_VALUES.has(v));
      if (filtered.length > 0) {
        values[param.name] = filtered;
      }
    } else {
      const raw = searchParams.get(param.name);
      if (raw === null || INVALID_PARAM_VALUES.has(raw)) continue;
      // Static select: drop a URL value that isn't a real option so the computed default wins (dynamic options load later).
      if (param.type === 'select' && !isDynamicParam(param) && param.options?.length) {
        if (!filterValidParamOptions(param.options).some((o) => o.value === raw)) continue;
      }
      values[param.name] = raw;
    }
  }
  return values;
}

/** True when options are fetched at runtime (SQL or public `hasDynamicOptions` flag). */
function isDynamicParam(param: DashboardParam): boolean {
  return !!param.sql || param.hasDynamicOptions === true;
}

/**
 * Build URL patches for dynamic params once options load: seed a missing/invalid select to its default
 * (or first option), and fill an empty multi_select to all options (so it isn't an empty panel).
 */
export function buildDeferredParamPatch(
  searchParams: URLSearchParams,
  parameters: DashboardParam[],
): Record<string, string | string[]> {
  const patch: Record<string, string | string[]> = {};
  for (const p of parameters) {
    if (!isDynamicParam(p)) continue;
    const options = filterValidParamOptions(p.options);
    if (options.length === 0) continue;

    if (p.type === 'select') {
      const currentVal = searchParams.get(p.name);
      const hasValidValue = currentVal !== null && options.some((o) => o.value === currentVal);
      if (!hasValidValue) {
        const fallback = resolveSelectDefault(p.default, options);
        if (fallback !== undefined) patch[p.name] = fallback;
      }
    } else if (p.type === 'multi_select') {
      const currentVals = searchParams.getAll(p.name);
      const validVals = currentVals.filter((v) => options.some((o) => o.value === v));
      if (validVals.length === 0) {
        // Empty/all-invalid → fill a dynamic multi_select to all options once they load (old behavior).
        patch[p.name] = options.map((o) => o.value);
      }
    }
  }
  return patch;
}

/** Serialize param values to URLSearchParams, only writing actual strings. */
export function valuesToSearchParams(values: Record<string, string | string[]>): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, val] of Object.entries(values)) {
    if (Array.isArray(val)) {
      for (const v of val) {
        if (typeof v === 'string' && v !== '') params.append(key, v);
      }
    } else if (typeof val === 'string' && val !== '') {
      params.set(key, val);
    }
  }
  return params;
}
